import React, { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { useMutation } from '@betterment/js-runtime';
import { Banner, Box, Button, Dialog, H1, H3, Text } from '@betterment/samba-react';

import { ApplyCashFirstBillingModalTable } from './ApplyCashFirstBillingModalTable';

import {
  BillingPlanPaymentTypeInput,
  PaymentType,
  UpdateBillingPlanPaymentTypesMutationInput,
} from '../../../../graphql/schema';
import urlBuilder from '../../../../Shared/url-builder';
import { pluralize } from '../../../../utils/strings/strings';
import { ApplyCashFirstBillingModalProps } from '../data';
import { UpdateBillingPlanPaymentTypesDocument } from '../graphql/mutation.updateBillingPlanPaymentTypes.generated';

export const ApplyCashFirstBillingModal: React.FC<ApplyCashFirstBillingModalProps> = ({
  billingPlans,
  isOpen,
  closeHandler,
  cardName,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [submitPaymentChanges] = useGraphQLMutation(UpdateBillingPlanPaymentTypesDocument);
  const [postDismissedCard] = useMutation(urlBuilder.postDismissedApplyCashFirstBillingCard.absolute(), {
    method: 'post',
  });

  const handleCheckboxChange = (value: string) => {
    if (selectedOptions.includes(value)) {
      setSelectedOptions(selectedOptions.filter(option => option !== value));
    } else {
      setSelectedOptions([...selectedOptions, value]);
    }
  };

  const filteredBillingPlans = billingPlans.filter(plan => plan.paymentType !== PaymentType.CashReserveFirst);
  const isAllFilteredBillingPlansSelected = selectedOptions.length === filteredBillingPlans.length;

  const handleSelectAll = () => {
    setSelectedOptions(isAllFilteredBillingPlansSelected ? [] : [...filteredBillingPlans.map(plan => plan.id)]);
  };

  const submitPayments = () => {
    const payload: UpdateBillingPlanPaymentTypesMutationInput = {
      billingPlanPaymentTypes: selectedOptions.map<BillingPlanPaymentTypeInput>(id => {
        return { id, paymentType: PaymentType.CashReserveFirst };
      }),
    };

    return submitPaymentChanges({ variables: { input: payload } });
  };

  const handleSubmit = async () => {
    if (selectedOptions.length === 0) return;

    const { data } = await submitPayments();
    const { displayableErrors } = data!.updateBillingPlanPaymentTypes;
    if (displayableErrors.length === 0) {
      const { errors } = await postDismissedCard({ card: cardName });
      if (!errors) {
        window.location.reload();
      }
    }
  };

  return (
    <Dialog data-testid="applyCashModal" open={isOpen} overlay="default" onOpenChange={closeHandler}>
      <Dialog.Content
        css={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: '640px',
          p: 'space.400',
          overflow: 'auto',
          pb: 'space.0',
        }}
      >
        <Dialog.Title asChild={true}>
          <H1 variant="title300" css={{ pb: 'space.150' }}>
            Select plans to apply Cash Reserve first billing
          </H1>
        </Dialog.Title>
        <Text>
          Choose the plans you'd like to update to bill fees from Cash Reserve first accounts. The following
          restrictions apply:
          <ul>
            <li>Standard 401(k) plan fees are not eligible for assessment from cash accounts</li>
            <li>
              Fees for individual and joint investing accounts may only be assessed from cash accounts that share an
              owner
            </li>
            <li>
              Fees for investing trust accounts may only be assessed from cash trust accounts that share a taxpayer
              identification number
            </li>
          </ul>
        </Text>
        <Dialog.Description asChild={true}>
          <Box css={{ mb: 'space.400', mt: 'space.300' }}>
            <Box css={{ display: 'flex', justifyContent: 'space-between', p: 'space.100', pl: 'space.0' }}>
              <Box>
                <H3 variant="title100">Billing plans</H3>
              </Box>
              <Box>
                <Button data-testid="selectAllToggle" onClick={handleSelectAll} variant="textPrimary">
                  {isAllFilteredBillingPlansSelected ? 'Unselect all' : 'Select all'}
                </Button>
              </Box>
            </Box>
            <ApplyCashFirstBillingModalTable
              billingPlans={filteredBillingPlans}
              handleCheckboxChange={handleCheckboxChange}
              selectedOptions={selectedOptions}
            />
          </Box>
        </Dialog.Description>
        <Box
          css={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.surface',
            pb: 'space.400',
            pt: 'space.200',
          }}
        >
          <Banner variant="info" css={{ mb: 'space.400' }}>
            <Banner.Content>
              <Text as="p">
                If the full amount isn't available, we'll bill in the following order: first from Cash Reserve accounts,
                starting with the largest balance, then from taxable accounts in order of largest balance, and finally
                from tax-advantaged accounts where the fees were accrued.
              </Text>
            </Banner.Content>
          </Banner>
          <Button variant="solidPrimary" onClick={() => void handleSubmit()} disabled={selectedOptions.length === 0}>
            Update {selectedOptions.length > 0 && pluralize(selectedOptions.length, 'plan')}
          </Button>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};
